import React, { useEffect, useState } from 'react';
import ShowcaseReel from './ShowcaseReel';
import { onePieceBoard } from './onepiecequiz-leaderboard-data';

const SITE_URL = 'https://www.onepiecetrivia.com';
const BOARD_URL = 'https://www.onepiecetrivia.com/leaderboard';

const ACCENT = '#ff4b2b';
const ACCENT_GRADIENT = 'linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%)';

// Starts from the saved snapshot, then loadLiveBoard() swaps in the live board (/api/onepiece-board, cached 15 min).
let { totals, games, grandMasters, regulars, monthly, capturedAt } = onePieceBoard;
export async function loadLiveBoard() {
  try {
    const r = await fetch('/api/onepiece-board');
    if (!r.ok) return false;
    const d = await r.json();
    // never replace a fuller board with a thinner one (an upstream hiccup must not wipe the page)
    if (!d || !d.totals || d.totals.plays < totals.plays * 0.8) return false;
    ({ totals, games, grandMasters, regulars, monthly, capturedAt } = d);
    onepiecequizContent.callToAction.text = `${totals.rankedPlayers} players are already on the board. Post a run and find out where you land.`;
    return true;
  } catch (e) { return false; }
}

/* ------------------------------------------------------------ helpers --- */

const formatTime = (seconds) =>
  seconds >= 60
    ? `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
    : `${seconds}s`;

const formatDay = (iso) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

const formatMonth = (ym) =>
  new Date(`${ym}-01T00:00:00Z`).toLocaleDateString('en-GB', {
    month: 'short', timeZone: 'UTC',
  });

const medal = (rank) => ({ 1: '🥇', 2: '🥈', 3: '🥉' }[rank] || null);

// Phones get a denser layout and a shorter board. Everything that only changes
// how something looks lives in the stylesheet below; this hook is only for the
// two places where the markup itself has to differ.
const useNarrow = () => {
  const [narrow, setNarrow] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 620px)');
    const sync = () => setNarrow(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  return narrow;
};

/* --------------------------------------------------------- stylesheet --- */
// Inline styles cannot hold media queries, and without them the whole page
// collapses into one column on a phone — nine screens of scrolling. Anything
// that has to respond to width is a class.

const PageStyles = () => (
  <style>{`
    @keyframes opPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.25; } }
    @keyframes opSpin { to { transform: rotate(360deg); } }

    .op-grid { display: grid; }
    .op-stats   { grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 14px; }
    .op-players { grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; }
    .op-cards   { grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 16px; }
    .op-shots   { grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px; }

    .op-row {
      display: grid;
      grid-template-columns: 52px 1fr auto;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      border-radius: 10px;
      border-bottom: 1px solid var(--border-color);
    }
    .op-row-top { background: rgba(255, 75, 43, 0.06); }

    .op-frame { height: clamp(460px, 78vh, 820px); }

    @media (max-width: 620px) {
      /* Two across beats one long column — the whole point is seeing more
         of the page at once, not scrolling past it. */
      .op-stats   { grid-template-columns: 1fr 1fr; gap: 10px; }
      .op-players { grid-template-columns: 1fr 1fr; gap: 8px; }
      .op-cards   { grid-template-columns: 1fr 1fr; gap: 10px; }

      .op-tile       { padding: 13px 12px !important; }
      .op-tile-value { font-size: 1.45rem !important; }
      .op-tile-label { font-size: 10px !important; letter-spacing: 0.06em !important; }
      .op-tile-sub   { font-size: 10.5px !important; line-height: 1.4 !important; }

      .op-player       { padding: 9px !important; gap: 9px !important; }
      .op-player-face  { width: 30px !important; height: 30px !important; font-size: 15px !important; }
      .op-player-name  { font-size: 12.5px !important; }
      .op-player-note  { font-size: 11px !important; }

      .op-card      { padding: 13px !important; }
      .op-card-head { font-size: 13px !important; margin-bottom: 6px !important; }
      .op-card-body { font-size: 11.5px !important; line-height: 1.5 !important; }

      .op-row       { grid-template-columns: 30px 1fr auto; gap: 8px; padding: 8px 4px; }
      .op-row-face  { width: 28px !important; height: 28px !important; font-size: 14px !important; }
      .op-row-name  { font-size: 12.5px !important; }
      .op-row-note  { font-size: 10.5px !important; }
      .op-row-score { font-size: 13.5px !important; }
      .op-row-cap   { font-size: 10.5px !important; }
      .op-rank      { font-size: 12px !important; }
      .op-rank-medal{ font-size: 16px !important; }

      .op-tab { padding: 7px 12px !important; font-size: 11.5px !important; gap: 5px !important; }

      .op-head       { padding-bottom: 10px !important; margin-bottom: 16px !important; gap: 10px !important; }
      .op-head-title { font-size: 1.2rem !important; }
      .op-head-note  { display: none; }

      .op-frame { height: 56vh; min-height: 360px; }
      .op-panel { padding: 12px 10px !important; }
      .op-note  { font-size: 11.5px !important; }
    }

    /* Very narrow phones cannot carry two columns of prose. */
    @media (max-width: 379px) {
      .op-cards { grid-template-columns: 1fr; }
    }
  `}</style>
);

/* ------------------------------------------------------------- pieces --- */

const Avatar = ({ value, size = 34, className }) => {
  const shared = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    flexShrink: 0,
    border: '1px solid var(--border-color)',
    background: 'var(--bg-accent)',
  };

  if (typeof value === 'string' && value.startsWith('http')) {
    return (
      <img
        src={value}
        alt=""
        loading="lazy"
        className={className}
        style={{ ...shared, objectFit: 'cover' }}
      />
    );
  }

  return (
    <span className={className} style={{
      ...shared,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: `${Math.round(size * 0.5)}px`,
      lineHeight: 1,
    }}>
      {value || '🏴‍☠️'}
    </span>
  );
};

const SectionHeader = ({ index, title, note }) => (
  <div className="op-head" style={{
    display: 'flex',
    alignItems: 'baseline',
    gap: '14px',
    flexWrap: 'wrap',
    borderBottom: '1px solid var(--border-color)',
    paddingBottom: '14px',
    marginBottom: '28px',
  }}>
    <span style={{
      fontSize: '12px',
      fontWeight: '700',
      letterSpacing: '0.16em',
      color: ACCENT,
    }}>
      {index}
    </span>
    <h2 className="op-head-title" style={{
      fontSize: 'clamp(1.35rem, 3vw, 1.9rem)',
      fontWeight: '700',
      color: 'var(--text-primary)',
      margin: 0,
      flex: 1,
    }}>
      {title}
    </h2>
    {note && (
      <span className="op-head-note" style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
        {note}
      </span>
    )}
  </div>
);

/* --------------------------------------------------------------- hero --- */

const HeroButton = ({ href, children, primary }) => {
  const [hover, setHover] = useState(false);

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: primary ? ACCENT_GRADIENT : 'var(--bg-secondary)',
        color: primary ? '#fff' : 'var(--text-primary)',
        border: primary ? '1px solid transparent' : '1px solid var(--border-color)',
        padding: '12px 24px',
        borderRadius: '10px',
        textDecoration: 'none',
        fontWeight: '600',
        fontSize: '14px',
        transition: 'transform 0.2s ease',
        transform: hover ? 'translateY(-2px)' : 'none',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </a>
  );
};

const Hero = () => (
  <div style={{ textAlign: 'center', padding: 'clamp(16px, 4vw, 48px) 0 0' }}>
    <PageStyles />

    <img
      src="/onepiecequiz-icon.png"
      alt="One Piece Trivia"
      style={{
        width: 'clamp(64px, 10vw, 100px)',
        height: 'clamp(64px, 10vw, 100px)',
        borderRadius: '50%',
        display: 'block',
        margin: '0 auto 16px',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.25)',
      }}
    />

    <span style={{
      background: ACCENT_GRADIENT,
      color: '#fff',
      padding: '5px 14px',
      borderRadius: '20px',
      fontSize: '11px',
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: '0.12em',
      display: 'inline-block',
      marginBottom: '16px',
    }}>
      Anime Trivia
    </span>

    <h1 style={{
      fontSize: 'clamp(1.9rem, 6vw, 4rem)',
      fontWeight: '800',
      color: 'var(--text-primary)',
      margin: '0 0 14px',
      lineHeight: '1.05',
    }}>
      One Piece Trivia
    </h1>

    <p style={{
      fontSize: 'clamp(0.9rem, 2vw, 1.15rem)',
      color: 'var(--text-secondary)',
      maxWidth: '640px',
      margin: '0 auto 22px',
      lineHeight: '1.6',
    }}>
      A home for One Piece fans: quizzes on characters, Devil Fruits and faces,
      Bounty Battle, a live jigsaw race with friends, WANTED posters and your own
      crew — every quiz run is scored, timed and ranked on one shared board.
    </p>

    <div style={{
      display: 'flex',
      gap: '10px',
      flexWrap: 'wrap',
      justifyContent: 'center',
      marginBottom: '20px',
    }}>
      <HeroButton href={SITE_URL} primary>Play it live ↗</HeroButton>
      <HeroButton href={BOARD_URL}>See the full board ↗</HeroButton>
    </div>

    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '9px',
      padding: '7px 15px',
      borderRadius: '999px',
      background: 'rgba(16, 185, 129, 0.08)',
      border: '1px solid rgba(16, 185, 129, 0.25)',
      color: '#10b981',
      fontSize: '12.5px',
      fontWeight: '600',
    }}>
      <span style={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        background: '#10b981',
        animation: 'opPulse 1.8s ease-in-out infinite',
      }} />
      Live · {totals.plays} runs from {totals.rankedPlayers} players
    </div>
  </div>
);

/* -------------------------------------------------------------- pulse --- */

const StatTile = ({ value, label, sub }) => (
  <div className="op-tile" style={{
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border-color)',
    borderRadius: '14px',
    padding: '20px 18px',
  }}>
    <div className="op-tile-value" style={{
      fontSize: 'clamp(1.8rem, 4vw, 2.4rem)',
      fontWeight: '800',
      color: 'var(--text-primary)',
      lineHeight: '1.1',
    }}>
      {value}
    </div>
    <div className="op-tile-label" style={{
      fontSize: '12px',
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      color: 'var(--text-primary)',
      marginTop: '8px',
    }}>
      {label}
    </div>
    <div className="op-tile-sub" style={{
      fontSize: '12px',
      color: 'var(--text-secondary)',
      marginTop: '4px',
    }}>
      {sub}
    </div>
  </div>
);

const PulseStrip = () => (
  <div>
    <div className="op-grid op-stats">
      <StatTile value={totals.plays} label="Runs posted" sub="every scored game, all time" />
      <StatTile value={totals.rankedPlayers} label="Players ranked" sub="everyone holding a score on the board" />
      <StatTile value={`${totals.activeShare}%`} label="Still playing" sub="of them posted a run in the last 30 days" />
      <StatTile value={totals.plays30d} label="Runs this month" sub={`${totals.recentShare}% of everything ever posted`} />
      <StatTile value={`${totals.accuracy}%`} label="Answer accuracy" sub="across every question served" />
      <StatTile value={totals.perfectRounds} label="Perfect rounds" sub="full marks, no mistakes" />
    </div>

    <p className="op-note" style={{
      fontSize: '12px',
      color: 'var(--text-secondary)',
      marginTop: '14px',
      lineHeight: '1.6',
    }}>
      Live from the board, updated {formatDay(capturedAt)}{' '}
      {new Date(capturedAt).getUTCFullYear()}. First run posted{' '}
      {formatDay(totals.firstPlay)}, most recent {formatDay(totals.latestPlay)}.
    </p>
  </div>
);

/* ----------------------------------------------------------- activity --- */

const ActivityChart = () => {
  // Cumulative runs on the board, month by month. The shape is the story —
  // the raw monthly counts are noise next to it.
  let running = 0;
  const points = monthly.map((month) => {
    running += month.plays;
    return { month: month.month, total: running };
  });

  const W = 600;
  const H = 200;
  const peak = points[points.length - 1].total;
  const x = (i) => (i / (points.length - 1)) * W;
  const y = (total) => H - (total / peak) * (H - 20);

  const line = points.map((p, i) => `${x(i)},${y(p.total)}`).join(' ');
  const area = `${x(0)},${H} ${line} ${x(points.length - 1)},${H}`;

  return (
    <div className="op-panel" style={{
      background: 'var(--bg-secondary)',
      border: '1px solid var(--border-color)',
      borderRadius: '14px',
      padding: 'clamp(20px, 3vw, 28px)',
    }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Runs posted to the leaderboard, growing month by month"
        style={{ width: '100%', height: 'clamp(120px, 26vw, 220px)', display: 'block' }}
      >
        <defs>
          <linearGradient id="opFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ff416c" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#ff4b2b" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <polygon points={area} fill="url(#opFill)" />
        <polyline
          points={line}
          fill="none"
          stroke={ACCENT}
          strokeWidth="3"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
        {points.map((p) => (
          <span key={p.month} className="op-note" style={{
            fontSize: '12px',
            color: 'var(--text-secondary)',
          }}>
            {formatMonth(p.month)}
          </span>
        ))}
      </div>

      <p className="op-note" style={{
        fontSize: '13px',
        color: 'var(--text-secondary)',
        margin: '16px 0 0',
        lineHeight: '1.6',
        borderTop: '1px solid var(--border-color)',
        paddingTop: '14px',
      }}>
        The board only grows. It started quiet and then caught on — two thirds of
        every run on it was posted in the last two months. The players below are
        the regulars: the ones who keep coming back for another run.
      </p>
    </div>
  );
};

const Regulars = () => (
  <div className="op-grid op-players">
    {regulars.map((player) => (
      <div key={player.username} className="op-player" style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '14px',
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border-color)',
        borderRadius: '12px',
      }}>
        <Avatar value={player.avatar} size={38} className="op-player-face" />
        <div style={{ minWidth: 0 }}>
          <div className="op-player-name" style={{
            fontSize: '14px',
            fontWeight: '700',
            color: 'var(--text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {player.username}
          </div>
          <div className="op-player-note" style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            {player.plays} runs · best {player.best}
          </div>
        </div>
      </div>
    ))}
  </div>
);

/* -------------------------------------------------------------- board --- */

const TABS = [
  ...games.map((game) => ({ id: game.id, label: game.label, emoji: game.emoji })),
  { id: 'grand-masters', label: 'Grand Masters', emoji: '👑' },
];

const Tab = ({ tab, active, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    className="op-tab"
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 18px',
      borderRadius: '999px',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '600',
      fontFamily: 'inherit',
      whiteSpace: 'nowrap',
      transition: 'all 0.2s ease',
      background: active ? ACCENT_GRADIENT : 'var(--bg-secondary)',
      color: active ? '#fff' : 'var(--text-secondary)',
      border: active ? '1px solid transparent' : '1px solid var(--border-color)',
    }}
  >
    <span aria-hidden="true">{tab.emoji}</span>
    {tab.label}
  </button>
);

const Row = ({ children, highlight }) => (
  <div className={`op-row${highlight ? ' op-row-top' : ''}`}>{children}</div>
);

const RankCell = ({ rank }) => (
  <span className={medal(rank) ? 'op-rank-medal' : 'op-rank'} style={{
    fontSize: medal(rank) ? '20px' : '14px',
    fontWeight: '700',
    color: 'var(--text-secondary)',
    textAlign: 'center',
  }}>
    {medal(rank) || rank}
  </span>
);

const NameCell = ({ avatar, username, note }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
    <Avatar value={avatar} className="op-row-face" />
    <div style={{ minWidth: 0 }}>
      <div className="op-row-name" style={{
        fontSize: '14px',
        fontWeight: '700',
        color: 'var(--text-primary)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {username}
      </div>
      <div className="op-row-note" style={{
        fontSize: '12px',
        color: 'var(--text-secondary)',
        whiteSpace: 'nowrap',
      }}>
        {note}
      </div>
    </div>
  </div>
);

const ScoreCell = ({ score, max, caption }) => (
  <div style={{ textAlign: 'right' }}>
    <div className="op-row-score" style={{
      fontSize: '15px',
      fontWeight: '800',
      color: 'var(--text-primary)',
    }}>
      {score}
      <span style={{ fontSize: '0.75em', fontWeight: '500', color: 'var(--text-secondary)' }}>
        /{max}
      </span>
    </div>
    <div className="op-row-cap" style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
      {caption}
    </div>
  </div>
);

const ShowAll = ({ hidden, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      width: '100%',
      marginTop: '10px',
      padding: '10px',
      borderRadius: '10px',
      cursor: 'pointer',
      fontFamily: 'inherit',
      fontSize: '12.5px',
      fontWeight: '700',
      color: 'var(--text-primary)',
      background: 'var(--bg-secondary)',
      border: '1px solid var(--border-color)',
    }}
  >
    Show {hidden} more
  </button>
);

const GameBoard = ({ game, limit }) => (
  <>
    <p className="op-note" style={{
      fontSize: '13px',
      color: 'var(--text-secondary)',
      margin: '0 0 10px',
    }}>
      {game.plays} runs · ranked by score, then by the faster clock
    </p>
    {game.top.slice(0, limit).map((entry) => (
      <Row key={`${entry.username}-${entry.date}`} highlight={entry.rank <= 3}>
        <RankCell rank={entry.rank} />
        <NameCell
          avatar={entry.avatar}
          username={entry.username}
          note={formatDay(entry.date)}
        />
        <ScoreCell
          score={entry.score}
          max={game.maxScore}
          caption={formatTime(entry.totalTime)}
        />
      </Row>
    ))}
  </>
);

const GrandMasterBoard = ({ limit, narrow }) => (
  <>
    <p className="op-note" style={{
      fontSize: '13px',
      color: 'var(--text-secondary)',
      margin: '0 0 10px',
    }}>
      {grandMasters.length} players have scored 11 or better in all four games —
      the hardest thing to do on the site. Ranked by combined best, out of 56.
    </p>
    {grandMasters.slice(0, limit).map((player, i) => (
      <Row key={player.username} highlight={i < 3}>
        <RankCell rank={i + 1} />
        <NameCell
          avatar={player.avatar}
          username={player.username}
          note={games.map((g) => `${g.emoji}${player.scores[g.id]}`).join(narrow ? ' ' : '  ')}
        />
        <ScoreCell score={player.total} max={56} caption={narrow ? 'best' : 'combined best'} />
      </Row>
    ))}
  </>
);

const Board = () => {
  const [active, setActive] = useState(TABS[0].id);
  const [expanded, setExpanded] = useState(false);
  const narrow = useNarrow();

  const game = games.find((g) => g.id === active);
  const total = game ? game.top.length : grandMasters.length;
  const limit = narrow && !expanded ? 5 : total;
  const hidden = total - limit;

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
        {TABS.map((tab) => (
          <Tab
            key={tab.id}
            tab={tab}
            active={tab.id === active}
            onSelect={() => { setActive(tab.id); setExpanded(false); }}
          />
        ))}
      </div>

      <div className="op-panel" style={{
        background: 'var(--bg-primary)',
        border: '1px solid var(--border-color)',
        borderRadius: '14px',
        padding: 'clamp(16px, 3vw, 24px)',
      }}>
        {game
          ? <GameBoard game={game} limit={limit} />
          : <GrandMasterBoard limit={limit} narrow={narrow} />}

        {hidden > 0 && <ShowAll hidden={hidden} onClick={() => setExpanded(true)} />}
      </div>

      <p className="op-note" style={{
        fontSize: '12px',
        color: 'var(--text-secondary)',
        marginTop: '12px',
        lineHeight: '1.6',
      }}>
        Top ten of each board, mirroring the live ranking rules. Strong players
        show up twice when two of their runs both beat the field.{' '}
        <a
          href={BOARD_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--text-primary)', fontWeight: '600' }}
        >
          Full board on the site ↗
        </a>
      </p>
    </div>
  );
};

/* ---------------------------------------------------------- live site --- */

const LiveSite = () => {
  const [loaded, setLoaded] = useState(false);

  // A cached iframe can finish loading before React attaches onLoad, so the
  // event never arrives and the placeholder would sit there for good.
  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 6000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div>
      <div style={{
        borderRadius: '14px',
        overflow: 'hidden',
        border: '1px solid var(--border-color)',
        background: '#000',
        boxShadow: '0 24px 60px rgba(0, 0, 0, 0.28)',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '9px 12px',
          background: 'var(--bg-secondary)',
          borderBottom: '1px solid var(--border-color)',
        }}>
          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
            {['#ff5f57', '#febc2e', '#28c840'].map((c) => (
              <span key={c} style={{
                width: '11px',
                height: '11px',
                borderRadius: '50%',
                background: c,
                display: 'block',
              }} />
            ))}
          </div>
          <div style={{
            flex: 1,
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-color)',
            borderRadius: '6px',
            padding: '5px 12px',
            fontSize: '12px',
            color: 'var(--text-secondary)',
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            onepiecetrivia.com/puzzle
          </div>
          <a
            href={SITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: '12px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              textDecoration: 'none',
              flexShrink: 0,
            }}
          >
            Open ↗
          </a>
        </div>

        <div className="op-frame" style={{ position: 'relative', width: '100%', background: '#000' }}>
          {!loaded && (
            <div style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '14px',
              zIndex: 0,
              color: 'rgba(255,255,255,0.5)',
              fontSize: '12px',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}>
              <span style={{
                width: '26px',
                height: '26px',
                border: '2px solid rgba(255,255,255,0.18)',
                borderTopColor: 'rgba(255,255,255,0.7)',
                borderRadius: '50%',
                animation: 'opSpin 0.9s linear infinite',
              }} />
              Loading the live site
            </div>
          )}
          <iframe
            src={`${SITE_URL}/puzzle`}
            title="One Piece Trivia — live website"
            loading="lazy"
            onLoad={() => setLoaded(true)}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              border: 'none',
              display: 'block',
              zIndex: 1,
            }}
          />
        </div>
      </div>

      <p className="op-note" style={{
        fontSize: '13px',
        color: 'var(--text-secondary)',
        textAlign: 'center',
        marginTop: '14px',
      }}>
        That is the real site running live above. If your browser blocks embedded
        frames,{' '}
        <a
          href={SITE_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--text-primary)', fontWeight: '600' }}
        >
          open onepiecetrivia.com in a new tab ↗
        </a>
      </p>
    </div>
  );
};

/* --------------------------------------------------------- screens+stack */

const Screens = () => {
  const shots = [
    { src: '/portfolio-screens/onepiecequiz/leaderboard2.png', label: 'The board, per game' },
    { src: '/portfolio-screens/onepiecequiz/leaderboard.png', label: 'Grand Master rankings' },
  ];

  return (
    <div className="op-grid op-shots">
      {shots.map((shot) => (
        <div key={shot.src} style={{ textAlign: 'center' }}>
          <img
            src={shot.src}
            alt={shot.label}
            loading="lazy"
            style={{
              width: '100%',
              borderRadius: '14px',
              border: '1px solid var(--border-color)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
            }}
          />
          <p className="op-note" style={{
            color: 'var(--text-secondary)',
            fontSize: '12px',
            marginTop: '8px',
          }}>
            {shot.label}
          </p>
        </div>
      ))}
    </div>
  );
};

const HowItWorks = () => {
  const notes = [
    {
      title: 'Many ways to play',
      body: 'Name the character, the Devil Fruit or the face in the panel, call the bounty higher or lower, race friends on a jigsaw, print your WANTED poster or build your crew. Every quiz lands on the same board.',
    },
    {
      title: 'Every run counts for something',
      body: 'A round is fifteen questions against a running clock. When it ends the score goes straight onto the board, so there is always someone above you to chase.',
    },
    {
      title: 'The clock is the real opponent',
      body: 'So many people finish with full marks that the score alone stopped separating them. Same score, faster run wins — that is what the top of every board comes down to now.',
    },
    {
      title: 'Nobody ever loses their place',
      body: 'Scores from the very first week are still sitting on the board. The history only grows — your best run stays yours.',
    },
    {
      title: 'The cast is the real cast',
      body: 'The characters and their faces are the real cast, far more of the crew than a handmade quiz could ever cover — from the Straw Hats to the deepest cuts.',
    },
    {
      title: 'Grand Master is meant to hurt',
      body: `Beat eleven in all four games and you get the crown. Out of ${totals.rankedPlayers} players who have posted a score, ${grandMasters.length} have ever managed it.`,
    },
  ];

  return (
    <div className="op-grid op-cards">
      {notes.map((note) => (
        <div key={note.title} className="op-card" style={{
          padding: '22px',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-color)',
          borderRadius: '14px',
        }}>
          <h3 className="op-card-head" style={{
            fontSize: '15px',
            fontWeight: '700',
            color: 'var(--text-primary)',
            margin: '0 0 10px',
          }}>
            {note.title}
          </h3>
          <p className="op-card-body" style={{
            fontSize: '13px',
            color: 'var(--text-secondary)',
            margin: 0,
            lineHeight: '1.7',
          }}>
            {note.body}
          </p>
        </div>
      ))}
    </div>
  );
};

/* --------------------------------------------------------------- page --- */

export const onepiecequizContent = {
  pageMaxWidth: '1200px',
  breadcrumb: {
    text: 'Back to Home',
    url: '/',
  },

  sections: [
    {
      id: 'hero',
      spacing: 'clamp(20px, 4vw, 48px)',
      blocks: [{ type: 'custom', render: () => <Hero /> }],
    },
    { id: 'showcase-motion', blocks: [{ type: 'custom', render: () => <ShowcaseReel slug="onepiecequiz" video alt="One Piece Trivia" /> }] },

    {
      id: 'live-site',
      spacing: 'clamp(24px, 5vw, 64px)',
      blocks: [
        { type: 'custom', render: () => <SectionHeader index="01" title="The Site, Live" note="onepiecetrivia.com" /> },
        { type: 'custom', render: () => <LiveSite /> },
      ],
    },

    {
      id: 'pulse',
      spacing: 'clamp(24px, 5vw, 64px)',
      blocks: [
        { type: 'custom', render: () => <SectionHeader index="02" title="The Board Right Now" note="live numbers" /> },
        { type: 'custom', render: () => <PulseStrip /> },
      ],
    },

    {
      id: 'leaderboard',
      spacing: 'clamp(24px, 5vw, 64px)',
      blocks: [
        { type: 'custom', render: () => <SectionHeader index="03" title="Who Is Winning" note="top 10 per game" /> },
        { type: 'custom', render: () => <Board /> },
      ],
    },

    {
      id: 'activity',
      spacing: 'clamp(24px, 5vw, 64px)',
      blocks: [
        { type: 'custom', render: () => <SectionHeader index="04" title="How Busy It Is" note="since launch" /> },
        { type: 'custom', render: () => <ActivityChart /> },
        { type: 'spacer', size: 'small' },
        { type: 'custom', render: () => <Regulars /> },
      ],
    },

    {
      id: 'screenshots',
      spacing: 'clamp(24px, 5vw, 64px)',
      blocks: [
        { type: 'custom', render: () => <SectionHeader index="05" title="Inside One Piece Quiz" /> },
        { type: 'custom', render: () => <Screens /> },
      ],
    },

    {
      id: 'how',
      spacing: 'clamp(24px, 5vw, 64px)',
      blocks: [
        { type: 'custom', render: () => <SectionHeader index="06" title="How It Plays" /> },
        { type: 'custom', render: () => <HowItWorks /> },
      ],
    },
  ],

  callToAction: {
    brand: { bg: "#1E3352", fg: "#FFFFFF", btnBg: "#FF4B2B", btnFg: "#FFFFFF" , logo: "/onepiecequiz-icon.png", logoWidth: 72, logoRadius: 36 },
    title: 'Think you know One Piece?',
    text: `${totals.rankedPlayers} players are already on the board. Post a run and find out where you land.`,
    button: { text: 'Play it live', url: SITE_URL },
  },
};
